import type React from 'react';
import { useCallback } from 'react';
import { hexToVec3 } from '../../utils';
import type { FormField } from '../../form-types';
import type { BaseShaderConfig } from '../../base-config';
import { TextureShader } from '../../texture-shader';
import {
  ShaderBehavior,
  ShaderCategory,
  type ShaderMetadata,
} from '../../shader-types';
import { useShaderPreview } from '../../hooks/useShaderPreview';

// 1. FireShader Model
export type FireShaderConfig = BaseShaderConfig & {
  name: 'fire';
  volatility: number;
  baseColor: string;
  topColor: string;
  backgroundColor: string;
  density: number;
};

export class FireShader extends TextureShader {
  name = 'fire' as const;
  volatility!: number;
  baseColor!: string;
  topColor!: string;
  backgroundColor!: string;
  width!: number;
  height!: number;
  referenceWidth!: number;
  referenceHeight!: number;
  density!: number;

  constructor(props: Partial<FireShader> = {}) {
    super();

    // Set defaults
    this.name = 'fire';
    this.volatility = 0.5;
    this.baseColor = '#ff0000';
    this.topColor = '#ffff00';
    this.backgroundColor = '#000000';
    this.width = 512;
    this.height = 512;
    this.referenceWidth = 512;
    this.referenceHeight = 512;
    this.density = 1.0;

    // Apply provided props
    Object.assign(this, props);
  }

  generate(): string {
    return `
            precision mediump float;
            ${this.getTextureUniforms()}
            uniform float u_volatility;
            uniform vec3 u_base_color;
            uniform vec3 u_top_color;
            uniform vec3 u_background_color;
            uniform float u_density;

            vec2 hash( vec2 p ) {
                p = vec2( dot(p,vec2(127.1,311.7)),
                          dot(p,vec2(269.5,183.3)) );
                return -1.0 + 2.0*fract(sin(p) * 43758.5453123);
            }

            float noise( in vec2 p ) {
                const float K1 = 0.366025404; // (sqrt(3)-1)/2;
                const float K2 = 0.211324865; // (3-sqrt(3))/6;
                vec2 i = floor( p + (p.x+p.y)*K1 );
                vec2 a = p - i + (i.x+i.y)*K2;
                vec2 o = step(a.yx,a.xy);
                vec2 b = a - o + K2;
                vec2 c = a - 1.0 + 2.0*K2;
                vec3 h = max( 0.5-vec3(dot(a,a), dot(b,b), dot(c,c) ), 0.0 );
                vec3 n = h*h*h*h*vec3( dot(a,hash(i+0.0)), dot(b,hash(i+o)), dot(c,hash(i+1.0)));
                return dot( n, vec3(70.0) );
            }

            float fbm ( in vec2 p ) {
                float f = 0.0;
                float a = 0.5;
                mat2 m = mat2( 1.6,  1.2, -1.2,  1.6 );
                // Dynamic octaves based on density for performance
                int octaves = int(2.0 + u_density * 2.0);
                for(int i = 0; i < 4; i++) {
                    if(i >= octaves) break;
                    f += a * noise(p);
                    p = m * p;
                    a *= 0.5;
                }
                return 0.5 + 0.5 * f;
            }

            void main() {
                ${this.getTextureUVPreamble()}

                // If we're outside the reference bounds, show background
                if (isOutOfBounds) {
                    gl_FragColor = vec4(u_background_color, 1.0);
                    return;
                }

                float t = u_time;

                // Enhanced turbulence with better motion
                vec2 motion1 = vec2(fbm(uv * 2.0 + t * u_volatility), fbm(uv * 2.0 + t * u_volatility + 10.0));
                vec2 motion2 = vec2(fbm(uv * 4.0 - t * u_volatility * 0.7), fbm(uv * 4.0 - t * u_volatility * 0.7 + 20.0));

                float noise1 = fbm(uv * 3.0 + motion1 * 0.3);
                float noise2 = fbm(uv * 6.0 + motion2 * 0.2);
                float combinedNoise = mix(noise1, noise2, 0.5);

                float gradient = 1.0 - uv.y;
                // Enhanced fire shape with better falloff
                float fire = pow(combinedNoise, 1.0 / u_density) * gradient;
                fire = smoothstep(0.1, 0.9, fire);

                // Better color mixing with temperature gradient
                float temperature = fire * gradient;
                vec3 color = mix(u_base_color, u_top_color, temperature);
                color = mix(u_background_color, color, fire);

                // Add some brightness variation
                color *= (1.0 + noise1 * 0.2);

                gl_FragColor = vec4(color, 1.0);
            }
        `;
  }

  static getFormConfig(): FormField[] {
    return [
      {
        type: 'range',
        name: 'volatility',
        label: 'Volatility',
        description: 'Fire intensity and movement',
        min: 0.1,
        max: 3.0,
        step: 0.1,
        defaultValue: 0.5,
        colspan: 1,
      },
      {
        type: 'range',
        name: 'density',
        label: 'Density',
        description: 'Fire density and complexity',
        min: 0.2,
        max: 3.0,
        step: 0.1,
        defaultValue: 1.0,
        colspan: 2, // Full width for this field
      },
      {
        type: 'color',
        name: 'baseColor',
        label: 'Base Color',
        description: 'Bottom flame color',
        defaultValue: '#ff0000',
        colspan: 1,
      },
      {
        type: 'color',
        name: 'topColor',
        label: 'Top Color',
        description: 'Top flame color',
        defaultValue: '#ffff00',
        colspan: 1,
      },
      {
        type: 'color',
        name: 'backgroundColor',
        label: 'Background Color',
        description: 'Background color behind the fire',
        defaultValue: '#000000',
        colspan: 2, // Full width for background color
      },
    ];
  }

  static getMetadata(): ShaderMetadata {
    return {
      behavior: ShaderBehavior.TEXTURE,
      category: ShaderCategory.EFFECTS,
      description:
        'Animated fire effect with customizable colors and intensity',
      tags: ['fire', 'flame', 'effect', 'animated', 'organic'],
    };
  }
}

// 2. FirePreview Component
export const FirePreview: React.FC<{ model: FireShader }> = ({ model }) => {
  const getUniformNames = useCallback(
    () => [
      'u_volatility',
      'u_base_color',
      'u_top_color',
      'u_background_color',
      'u_density',
    ],
    []
  );

  const updateUniforms = useCallback(
    (
      gl: WebGLRenderingContext,
      locations: Record<string, WebGLUniformLocation | null>,
      model: FireShader
    ) => {
      // Update shader-specific uniforms
      gl.uniform1f(locations.u_volatility, model.volatility);
      gl.uniform1f(locations.u_density, model.density);

      // Update color uniforms
      const baseColorVec3 = hexToVec3(model.baseColor);
      const topColorVec3 = hexToVec3(model.topColor);
      const backgroundColorVec3 = hexToVec3(model.backgroundColor);

      gl.uniform3fv(locations.u_base_color, baseColorVec3);
      gl.uniform3fv(locations.u_top_color, topColorVec3);
      gl.uniform3fv(locations.u_background_color, backgroundColorVec3);
    },
    []
  );

  const canvasRef = useShaderPreview(model, getUniformNames, updateUniforms);

  return <canvas ref={canvasRef} width={model.width} height={model.height} />;
};
